import type { AppointmentPlanningWorkOrderOverride } from "../../../_logic/appointments-contract"

const normalizeText = (value: unknown): string | null => {
  const text = `${value ?? ""}`.trim()

  return text || null
}

const normalizeBoolean = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null

export const compactAppointmentPlanningOverride = (
  override?: AppointmentPlanningWorkOrderOverride | null,
): AppointmentPlanningWorkOrderOverride | null => {
  if (!override) return null

  const next: AppointmentPlanningWorkOrderOverride = {}
  const appointmentEmployeeId = normalizeText(override.appointment_employee_id)
  const appointmentContactCustomerId = normalizeText(override.appointment_contact_customer_id)
  const flexAppointmentAllowed = normalizeBoolean(override.flex_appointment_allowed)
  const flexAppointmentMode = override.flex_appointment_mode
  const planningStrategy = override.planning_strategy
  const manualAppointmentType = override.manual_appointment_type
  const manualFlexDate = normalizeText(override.manual_flex_date)
  const manualFlexSlotId = normalizeText(override.manual_flex_slot_id)

  if (appointmentEmployeeId) next.appointment_employee_id = appointmentEmployeeId

  if (appointmentContactCustomerId)
    next.appointment_contact_customer_id = appointmentContactCustomerId

  if (flexAppointmentAllowed !== null) next.flex_appointment_allowed = flexAppointmentAllowed

  if (
    flexAppointmentMode === "DISABLED" ||
    flexAppointmentMode === "ALL" ||
    flexAppointmentMode === "EVERY_SECOND"
  ) {
    next.flex_appointment_mode = flexAppointmentMode
  }

  if (
    planningStrategy === "AUTO" ||
    planningStrategy === "MANUAL" ||
    planningStrategy === "KEEP_EXISTING"
  ) {
    next.planning_strategy = planningStrategy
  }

  if (manualAppointmentType === "HARD" || manualAppointmentType === "FLEX") {
    next.manual_appointment_type = manualAppointmentType
  }

  if (override.manual_appointment) next.manual_appointment = { ...override.manual_appointment }

  if (manualFlexDate) next.manual_flex_date = manualFlexDate

  if (manualFlexSlotId) next.manual_flex_slot_id = manualFlexSlotId

  if (override.manual_flex_appointment)
    next.manual_flex_appointment = { ...override.manual_flex_appointment }

  return Object.keys(next).length ? next : null
}

export const compactAppointmentPlanningOverrides = (
  overrides: Record<string, AppointmentPlanningWorkOrderOverride>,
): Record<string, AppointmentPlanningWorkOrderOverride> =>
  Object.fromEntries(
    Object.entries(overrides)
      .map(([rowId, override]) => [rowId, compactAppointmentPlanningOverride(override)] as const)
      .filter((entry): entry is [string, AppointmentPlanningWorkOrderOverride] =>
        Boolean(entry[1]),
      ),
  )

const resetOverrideFields = (
  overrides: Record<string, AppointmentPlanningWorkOrderOverride>,
  fields: Array<keyof AppointmentPlanningWorkOrderOverride>,
): Record<string, AppointmentPlanningWorkOrderOverride> =>
  compactAppointmentPlanningOverrides(
    Object.fromEntries(
      Object.entries(overrides).map(([rowId, override]) => {
        const next = { ...override }
        fields.forEach((field) => {
          delete next[field]
        })

        return [rowId, next]
      }),
    ),
  )

export const resetAppointmentEmployeeOverrides = (
  overrides: Record<string, AppointmentPlanningWorkOrderOverride>,
): Record<string, AppointmentPlanningWorkOrderOverride> =>
  resetOverrideFields(overrides, [
    "appointment_employee_id",
    "manual_appointment",
    "manual_flex_slot_id",
    "manual_flex_appointment",
  ])
